/**
 * Platform preferences API — per-user, per-app appearance settings.
 *
 * Backs the Aurora accent engine (A2) and the Settings → Appearance screen
 * (A4). Wraps `GET/PATCH /api/platform/preferences` (contract in
 * `packages/api/src/routes/platform/preferences.ts`): GET returns every
 * preference scope for the authed user, PATCH upserts one scope.
 *
 * Two consumers:
 *   - `useAccentPrefs()` → the per-app accent map fed to `useAppAccent`.
 *   - the Appearance screen + `TweaksPanel` (via __root) → read/write scopes.
 *
 * localStorage is kept as a first-paint / offline cache so the accent stamped
 * on the very first render matches the user's last choice (no flash). The
 * server is the source of truth and corrects the cache once GET resolves.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { landingRouteFor, type LandingApp, type LandingRoute } from '@k-os/core';
import { apiGet, apiSend } from './client';
import { useSession } from './auth-hooks';

/** `'platform'` (global scope) or an app slug carrying that app's accent. */
export type PreferenceScope = 'platform' | 'tasks' | 'health' | 'finance';

export interface UserPreferenceDto {
  scope: string;
  accentHex: string | null;
  theme: string | null;
  density: string | null;
  landingApp: string | null;
  updatedAt: string;
}

interface PreferencesResponse {
  preferences: UserPreferenceDto[];
}

/** PATCH body — upsert a single scope. Omitted fields are left untouched. */
export interface PreferencePatch {
  scope: PreferenceScope;
  accentHex?: string | null;
  theme?: string | null;
  density?: string | null;
  landingApp?: LandingApp | null;
}

const PREFS_KEY = ['platform', 'preferences'] as const;
const ACCENT_CACHE_KEY = 'k_os_accent_prefs';

// ---------------------------------------------------------------------------
// Query + mutation
// ---------------------------------------------------------------------------

/**
 * All preference scopes for the authed user. Gated on the session so it never
 * fires (and 401s) on the public login/signup pages.
 */
export function usePreferences() {
  const session = useSession();
  return useQuery({
    queryKey: PREFS_KEY,
    queryFn: () => apiGet<PreferencesResponse>('/platform/preferences'),
    enabled: !!session.data,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Upsert one scope. The server echoes the saved row; we splice it straight
 * into the cached list so accent / theme changes go live immediately without
 * waiting for a refetch.
 */
export function usePatchPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: PreferencePatch) =>
      apiSend<{ preference: UserPreferenceDto }>('PATCH', '/platform/preferences', patch),
    onSuccess: ({ preference }) => {
      qc.setQueryData<PreferencesResponse>(PREFS_KEY, (prev) => {
        const rows = prev?.preferences ?? [];
        const exists = rows.some((r) => r.scope === preference.scope);
        const next = exists
          ? rows.map((r) => (r.scope === preference.scope ? preference : r))
          : [...rows, preference];
        return { preferences: next };
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Derived selectors
// ---------------------------------------------------------------------------

function readAccentCache(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(ACCENT_CACHE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

/**
 * Per-app accent overrides keyed by scope (`'platform'` | app slug) → hex,
 * for `useAppAccent` (which falls back to Indigo for any absent scope).
 *
 * First-paint: returns the localStorage cache synchronously so the initial
 * `useAppAccent` effect stamps the right colour. Once GET resolves, the server
 * map takes over and is written back to the cache for the next cold start.
 */
export function useAccentPrefs(): Readonly<Record<string, string>> {
  const query = usePreferences();
  const [cache] = useState(readAccentCache);

  const serverMap = useMemo(() => {
    if (!query.data) return null;
    const m: Record<string, string> = {};
    for (const p of query.data.preferences) {
      if (p.accentHex) m[p.scope] = p.accentHex;
    }
    return m;
  }, [query.data]);

  useEffect(() => {
    if (!serverMap || typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(ACCENT_CACHE_KEY, JSON.stringify(serverMap));
    } catch {
      /* localStorage unavailable — the in-memory map still works this session */
    }
  }, [serverMap]);

  return serverMap ?? cache;
}

/** The `platform`-scope row (theme / density owner), if the user has one. */
export function usePlatformPref(): UserPreferenceDto | undefined {
  const query = usePreferences();
  return query.data?.preferences.find((p) => p.scope === 'platform');
}

/**
 * Where a just-signed-in user should land.
 *
 * The login flow needs this imperatively — it runs inside a submit handler,
 * after `login()` resolves, so it can't use the `usePreferences` hook. Shares
 * `PREFS_KEY` with that hook, so the fetch populates the same cache entry the
 * accent engine reads moments later rather than duplicating the request.
 *
 * Falls back to the picker on any failure: a preferences hiccup must never
 * strand a user who has successfully authenticated.
 */
export async function fetchLandingRoute(qc: QueryClient): Promise<LandingRoute> {
  try {
    const data = await qc.fetchQuery({
      queryKey: PREFS_KEY,
      queryFn: () => apiGet<PreferencesResponse>('/platform/preferences'),
    });
    const platform = data.preferences.find((p) => p.scope === 'platform');
    return landingRouteFor(platform?.landingApp);
  } catch {
    return '/apps';
  }
}
